import manifest, { missing } from "virtual:image-manifest";

// Raster images live in media/ and are addressed by a logical path ("/images/products/x.png").
// `imageUrl` turns that into a content-hashed URL: on imgix when VITE_IMGIX_DOMAIN is set,
// otherwise a same-origin file emitted by vite-plugins/cdn-images. Hashed keys never change
// meaning, so every URL is safe to cache for a year. See docs/images.md.
// Where vite-plugins/cdn-images emits hashed files when there is no imgix. Its own prefix lets
// public/_headers mark the whole directory immutable without catching un-hashed public/ files.
const FALLBACK_PREFIX = "/_img/";
// `npm run dev` without an imgix domain: vite-plugins/cdn-images/transform.ts emulates imgix on the
// same /_img/ URLs, so params work locally. The plugin sets this (and only in `vite dev`).
const LOCAL_TRANSFORMS = import.meta.env.VITE_LOCAL_IMAGE_TRANSFORMS === "true";
const IMGIX_ORIGIN = import.meta.env.VITE_IMGIX_DOMAIN ? `https://${import.meta.env.VITE_IMGIX_DOMAIN}` : null;

// imgix serves AVIF/WebP from the Accept header (auto=format) and recompresses (compress).
const BASE_PARAMS = "auto=format,compress";

/** Widest image we ever request — the source originals go up to 9 MB. */
export const MAX_IMAGE_WIDTH = 2560;
export const DEFAULT_WIDTHS = [480, 768, 1080, 1440, 1920, MAX_IMAGE_WIDTH];

export type ImageParams = { w?: number; h?: number; q?: number; fit?: "max" | "crop" | "clip" };

const variantsByKey = new Map<string, string[]>(Object.values(manifest).map(([key, variants]) => [key, variants]));

// Keys in media/ that imgix doesn't have yet. Only ever non-empty in `vite dev` with VITE_IMGIX_DOMAIN set
// (vite-plugins/cdn-images HEADs each key); those are served by the dev emulator below instead.
const NOT_ON_IMGIX = new Set(missing);

// Something at the URL applies our params if it's imgix, or if it's a /_img/ URL and the dev emulator is
// behind it (`vite dev` with no imgix domain, or with one but this key not uploaded yet). On the plain
// static fallback (built site, no imgix) params would be ignored, so those URLs stay bare.
const EMULATOR = LOCAL_TRANSFORMS || IMGIX_ORIGIN !== null;

/** True for a URL from `imageUrl` that the server will resize/re-encode according to its query. */
export function isTransformable(url: string) {
  return (IMGIX_ORIGIN !== null && url.startsWith(`${IMGIX_ORIGIN}/`)) || (EMULATOR && url.startsWith(FALLBACK_PREFIX));
}

/** Sets imgix params on a URL from `imageUrl`, replacing any already there. A no-op where nothing would apply them. */
export function withImageParams(url: string, params: ImageParams) {
  if (!isTransformable(url)) return url;
  const [base, query = ""] = url.split("?");
  const merged = new Map(query.split("&").filter(Boolean).map((kv) => kv.split("=") as [string, string]));
  for (const [k, v] of Object.entries(params)) if (v !== undefined) merged.set(k, String(v));
  return `${base}?${[...merged].map(([k, v]) => `${k}=${v}`).join("&")}`;
}

/**
 * `path` is a logical path under media/. On imgix the URL is capped at MAX_IMAGE_WIDTH (the source
 * originals go up to 9 MB), so even a bare `<img src={imageUrl(...)}>` is safe; pass `params` or use
 * `<Picture>` for responsive sizes.
 */
export function imageUrl(path: string, params: ImageParams = {}): string {
  const entry = manifest[path];
  if (!entry) {
    // Literals are checked at build time (vite-plugins/cdn-images); this catches dynamic paths.
    if (import.meta.env.PROD) {
      console.error(`imageUrl: "${path}" is not in media/`);
      return path;
    }
    throw new Error(`imageUrl: "${path}" is not in media/ — add it there or fix the path.`);
  }
  const key = entry[0];
  const base = IMGIX_ORIGIN !== null && !NOT_ON_IMGIX.has(key) ? `${IMGIX_ORIGIN}/${key}` : `${FALLBACK_PREFIX}${key}`;
  return isTransformable(base) ? withImageParams(`${base}?${BASE_PARAMS}`, { w: MAX_IMAGE_WIDTH, fit: "max", ...params }) : base;
}

/** URL of a pre-generated .avif/.webp twin of `url`, or null if there isn't one (always null where params are applied). */
export function imageVariantUrl(url: string, ext: "avif" | "webp") {
  if (isTransformable(url) || !url.startsWith(FALLBACK_PREFIX)) return null;
  const key = url.slice(FALLBACK_PREFIX.length);
  return variantsByKey.get(key)?.includes(ext) ? `${FALLBACK_PREFIX}${key.replace(/\.[^./]+$/, `.${ext}`)}` : null;
}

/** `srcset` with a `w` descriptor per width, never upscaling past the source. Only where params apply. */
export function imageSrcSet(url: string, widths: number[] = DEFAULT_WIDTHS) {
  if (!isTransformable(url)) return undefined;
  return widths.map((w) => `${withImageParams(url, { w, fit: "max" })} ${w}w`).join(", ");
}

/** `srcset` with 1x/2x/3x variants of an image rendered at a fixed CSS `width`. Only where params apply. */
export function imageDprSet(url: string, width: number) {
  if (!isTransformable(url)) return undefined;
  return [1, 2, 3]
    .filter((dpr) => width * dpr <= MAX_IMAGE_WIDTH)
    .map((dpr) => `${withImageParams(url, { w: width * dpr, fit: "max" })} ${dpr}x`)
    .join(", ");
}
